import { API, Auth, graphqlOperation, Hub } from 'aws-amplify';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { logout, setUser } from '../actions';
import { IState, IUser } from '../graphql/types';
import { useHistory } from 'react-router';
import { redirectToLogin } from '../components/utils';
import {
  GetPlanQuery,
  GetUserProfileQuery,
  OnUpdateUserPlanTimeStampSubscription,
} from '../graphql/api';
import { message } from 'antd';
import { getPlan, getUserProfile } from '../graphql/queries';
import { updateUserProfile } from '../graphql/mutations';

const useUser = () => {
  const { push } = useHistory();
  const user = useSelector<IState, IUser | undefined>(state => state.user);
  const dispatch = useDispatch();
  const isAdmin = user?.userRole === 'admin';
  const userFetching = useSelector<IState, boolean>(
    state => !!state.userFetching
  );
  const userPlan = useSelector<
    IState,
    | GetPlanQuery['getPlan']
    | OnUpdateUserPlanTimeStampSubscription['onUpdateUserPlanTimeStamp']
    | undefined
  >(state => state.user?.plan);
  const userHasAllowedRecording = useSelector<IState, boolean>(
    state => state.user?.plan?.features?.recording?.hasBrowserRecording ?? false
  );

  const loading = false;

  const refetch = React.useCallback(
    async data => {
      try {
        Hub.dispatch('user', {
          event: 'loadingUser',
          message: 'Loading user',
        });

        if (!data || Object.keys(data).length === 0) {
          try {
            await Auth.signOut();
          } catch (e) {
            Object.keys(localStorage).forEach(key => {
              if (
                key.startsWith('CognitoIdentityServiceProvider') ||
                key.startsWith('amplify')
              ) {
                localStorage.removeItem(key);
              }
            });
            dispatch(logout());
          }
          redirectToLogin(push);
          return;
        }

        let fullName = '';
        let email = '';
        let title = '';
        let preferences;
        let profileImageName = '';
        let planId = '';
        let plan;
        let profileImageService;
        let profileImageId;

        try {
          const [getUserResult, getPlanResult] = await Promise.all([
            API.graphql(graphqlOperation(getUserProfile)) as Promise<{
              data: GetUserProfileQuery;
            }>,
            API.graphql(graphqlOperation(getPlan)) as Promise<{
              data: GetPlanQuery;
            }>,
          ]);

          fullName = getUserResult.data.getUserProfile?.fullName || '';
          email = getUserResult.data.getUserProfile?.email || '';
          title = getUserResult.data.getUserProfile?.title || '';
          preferences = getUserResult.data.getUserProfile?.preferences || {};
          profileImageName =
            getUserResult.data.getUserProfile?.profileImageName || '';
          profileImageService =
            getUserResult.data.getUserProfile?.profileImageService || '';
          profileImageId =
            getUserResult.data.getUserProfile?.profileImageId || '';
          planId = getPlanResult?.data?.getPlan?.id || '';
          plan = getPlanResult.data.getPlan || {};
        } catch (e) {
          if (data) {
            await API.graphql(
              graphqlOperation(updateUserProfile, {
                input: {
                  role: data.attributes['custom:userRole'],
                  preferences: {},
                },
              })
            );
          }

          Hub.dispatch('user', {
            event: 'dataFetchFailed',
            data: {
              error: e,
            },
            message: 'Failed to fetch user data',
          });

          message.error('Failed to fetch user data');
        } finally {
          if (data) {
            Hub.dispatch('user', {
              event: 'userDataFetched',
              data: {
                data,
              },
              message: 'User data fetched',
            });
            dispatch(
              setUser({
                tenantID: data.attributes['custom:tenantID'],
                userId: data.username,
                userRole: data.attributes['custom:userRole'],
                fullName,
                email,
                title,
                preferences,
                profileImageName,
                profileImageService,
                profileImageId,
                planId,
                plan,
              })
            );
          }
        }
      } catch (e) {
        try {
          console.log(e);
          await Auth.signOut();
        } catch (err) {
          Object.keys(localStorage).forEach(key => {
            if (
              key.startsWith('CognitoIdentityServiceProvider') ||
              key.startsWith('amplify')
            ) {
              localStorage.removeItem(key);
            }
          });

          dispatch(logout());
          redirectToLogin(push);
        }
      }
    },
    [dispatch, push]
  );

  return {
    user,
    refetch,
    loading,
    isAdmin,
    userFetching,
    userPlan,
    userHasAllowedRecording,
  };
};

export default useUser;
